/**
 * WebSocket composable for real-time communication.
 * Manages WebSocket connection, message routing, and reconnection.
 */

import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

// Message types from backend
export enum MessageType {
  // Connection management
  CONNECT_ACK = 'connect_ack',
  PING = 'ping',
  PONG = 'pong',
  ERROR = 'error',
  SUBSCRIBE = 'subscribe',
  UNSUBSCRIBE = 'unsubscribe',

  // Telephony events
  CALL_INITIATED = 'call.initiated',
  CALL_RINGING = 'call.ringing',
  CALL_ANSWERED = 'call.answered',
  CALL_CONNECTED = 'call.connected',
  CALL_DISCONNECTED = 'call.disconnected',
  CALL_FAILED = 'call.failed',
  CALL_MUTED = 'call.muted',
  CALL_UNMUTED = 'call.unmuted',
  CALL_HELD = 'call.held',
  CALL_RESUMED = 'call.resumed',

  // Conference events
  CONFERENCE_CREATED = 'conference.created',
  CONFERENCE_PARTICIPANT_JOINED = 'conference.participant_joined',
  CONFERENCE_PARTICIPANT_LEFT = 'conference.participant_left',
  CONFERENCE_RECORDING_STARTED = 'conference.recording_started',
  CONFERENCE_RECORDING_STOPPED = 'conference.recording_stopped',
  CONFERENCE_ENDED = 'conference.ended',

  // Notification events
  NOTIFICATION_NEW = 'notification.new',
  NOTIFICATION_READ = 'notification.read',
  NOTIFICATION_DISMISSED = 'notification.dismissed',
  NOTIFICATION_COUNT_UPDATE = 'notification.count_update',

  // Hearing room events
  HEARING_STARTED = 'hearing.started',
  HEARING_ENDED = 'hearing.ended',
  HEARING_PARTICIPANT_STATUS = 'hearing.participant_status',
  HEARING_COMPLIANCE_UPDATE = 'hearing.compliance_update',
  HEARING_NOTE_UPDATED = 'hearing.note_updated',

  // Transcription events
  TRANSCRIPTION_SEGMENT = 'transcription.segment',
  TRANSCRIPTION_FINAL = 'transcription.final',
  TRANSCRIPTION_STARTED = 'transcription.started',
  TRANSCRIPTION_STOPPED = 'transcription.stopped',
  TRANSCRIPTION_ERROR = 'transcription.error',

  // AI Notes events
  AI_NOTES_STARTED = 'ai.notes.started',
  AI_NOTES_PROGRESS = 'ai.notes.progress',
  AI_NOTES_COMPLETED = 'ai.notes.completed',
  AI_NOTES_FAILED = 'ai.notes.failed',

  // AI Mentor events
  AI_MENTOR_SUGGESTION = 'ai.mentor.suggestion',
  AI_MENTOR_INSIGHT = 'ai.mentor.insight',
  AI_SENTIMENT_UPDATE = 'ai.sentiment.update',

  // Compliance auto-update from AI
  COMPLIANCE_AI_UPDATE = 'compliance.ai_update',

  // System events
  SYSTEM_ALERT = 'system.alert',
  SYSTEM_MAINTENANCE = 'system.maintenance',

  // Presence events
  PRESENCE_ONLINE = 'presence.online',
  PRESENCE_OFFLINE = 'presence.offline',
  PRESENCE_AWAY = 'presence.away',
}

export enum Channel {
  TELEPHONY = 'telephony',
  NOTIFICATION = 'notification',
  HEARING = 'hearing',
  SYSTEM = 'system',
  PRESENCE = 'presence',
}

export interface WebSocketMessage {
  id: string;
  type: MessageType | string;
  timestamp: string;
  channel: Channel | string;
  target?: {
    user_id?: string;
    tenant_id?: string;
    hearing_id?: string;
    role?: string;
  };
  payload: Record<string, any>;
}

export type MessageHandler = (message: WebSocketMessage) => void;

// Shared state
const socket = ref<WebSocket | null>(null);
const isConnected = ref(false);
const isConnecting = ref(false);
const connectionId = ref<string | null>(null);
const lastError = ref<string | null>(null);
const reconnectAttempts = ref(0);

// Message handlers organized by type/channel
const messageHandlers = new Map<string, Set<MessageHandler>>();
const channelHandlers = new Map<string, Set<MessageHandler>>();

// Configuration
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_BASE_DELAY = 1000; // 1 second
const PING_INTERVAL = 15000; // 15 seconds - more frequent to prevent Cloud Run timeout
const CONNECTION_TIMEOUT = 10000; // 10 seconds

let pingIntervalId: ReturnType<typeof setInterval> | null = null;
let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Get WebSocket URL based on API base URL
 */
function getWebSocketUrl(): string {
  const token = localStorage.getItem('access_token');
  if (!token) {
    throw new Error('No access token available');
  }

  // Use the API base URL to construct WebSocket URL (same as api.ts)
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const host = apiUrl.replace(/^https?:\/\//, '');

  return `${wsProtocol}://${host}/ws?token=${encodeURIComponent(token)}`;
}

/**
 * Connect to WebSocket server
 */
async function connect(): Promise<void> {
  if (socket.value?.readyState === WebSocket.OPEN) {
    return;
  }

  if (isConnecting.value) {
    return;
  }

  isConnecting.value = true;
  lastError.value = null;

  try {
    const wsUrl = getWebSocketUrl();
    const ws = new WebSocket(wsUrl);

    // Connection timeout
    const timeoutId = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        ws.close();
        lastError.value = 'Connection timeout';
        isConnecting.value = false;
        scheduleReconnect();
      }
    }, CONNECTION_TIMEOUT);

    ws.onopen = () => {
      clearTimeout(timeoutId);
      socket.value = ws;
      isConnected.value = true;
      isConnecting.value = false;
      reconnectAttempts.value = 0;
      startPingInterval();
      console.log('[WebSocket] Connected');
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        handleMessage(message);
      } catch (e) {
        console.error('[WebSocket] Failed to parse message:', e);
      }
    };

    ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      lastError.value = 'Connection error';
    };

    ws.onclose = (event) => {
      clearTimeout(timeoutId);
      socket.value = null;
      isConnected.value = false;
      isConnecting.value = false;
      connectionId.value = null;
      stopPingInterval();

      if (event.code !== 1000) {
        // Abnormal closure
        console.warn('[WebSocket] Connection closed:', event.code, event.reason);
        scheduleReconnect();
      } else {
        console.log('[WebSocket] Connection closed normally');
      }
    };
  } catch (e: any) {
    console.error('[WebSocket] Failed to connect:', e);
    lastError.value = e.message;
    isConnecting.value = false;
    scheduleReconnect();
  }
}

/**
 * Disconnect from WebSocket server
 */
function disconnect(): void {
  stopPingInterval();
  clearReconnectTimeout();

  if (socket.value) {
    socket.value.close(1000, 'Client disconnect');
    socket.value = null;
  }

  isConnected.value = false;
  isConnecting.value = false;
  connectionId.value = null;
  reconnectAttempts.value = 0;
}

/**
 * Schedule a reconnection attempt
 */
function scheduleReconnect(): void {
  if (reconnectAttempts.value >= MAX_RECONNECT_ATTEMPTS) {
    console.error('[WebSocket] Max reconnection attempts reached');
    lastError.value = 'Max reconnection attempts reached';
    return;
  }

  // Exponential backoff with jitter
  const delay = Math.min(
    RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttempts.value) + Math.random() * 1000,
    30000 // Max 30 seconds
  );

  console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.value + 1})`);

  reconnectTimeoutId = setTimeout(() => {
    reconnectAttempts.value++;
    connect();
  }, delay);
}

/**
 * Clear reconnect timeout
 */
function clearReconnectTimeout(): void {
  if (reconnectTimeoutId) {
    clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
  }
}

/**
 * Start ping interval to keep connection alive
 */
function startPingInterval(): void {
  stopPingInterval();
  pingIntervalId = setInterval(() => {
    if (socket.value?.readyState === WebSocket.OPEN) {
      send({ type: 'ping' });
    }
  }, PING_INTERVAL);
}

/**
 * Stop ping interval
 */
function stopPingInterval(): void {
  if (pingIntervalId) {
    clearInterval(pingIntervalId);
    pingIntervalId = null;
  }
}

/**
 * Send a message through WebSocket
 */
function send(message: Record<string, any>): void {
  if (socket.value?.readyState === WebSocket.OPEN) {
    socket.value.send(JSON.stringify(message));
  }
}

/**
 * Subscribe to a hearing room
 */
function subscribeToHearing(hearingId: string): void {
  send({
    type: 'subscribe',
    payload: { hearing_id: hearingId },
  });
}

/**
 * Unsubscribe from a hearing room
 */
function unsubscribeFromHearing(hearingId: string): void {
  send({
    type: 'unsubscribe',
    payload: { hearing_id: hearingId },
  });
}

/**
 * Handle incoming message
 */
function handleMessage(message: WebSocketMessage): void {
  // Handle connection acknowledgment
  if (message.type === MessageType.CONNECT_ACK) {
    connectionId.value = message.payload.connection_id;
    console.log('[WebSocket] Connection acknowledged:', connectionId.value);
    return;
  }

  // Handle pong (no action needed, just keeps connection alive)
  if (message.type === MessageType.PONG) {
    return;
  }

  // Dispatch to message type handlers
  const typeHandlers = messageHandlers.get(message.type);
  if (typeHandlers) {
    typeHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (e) {
        console.error('[WebSocket] Handler error:', e);
      }
    });
  }

  // Dispatch to channel handlers
  const channelHandlerSet = channelHandlers.get(message.channel);
  if (channelHandlerSet) {
    channelHandlerSet.forEach((handler) => {
      try {
        handler(message);
      } catch (e) {
        console.error('[WebSocket] Channel handler error:', e);
      }
    });
  }
}

/**
 * Register a handler for a specific message type
 */
function onMessage(type: string, handler: MessageHandler): () => void {
  if (!messageHandlers.has(type)) {
    messageHandlers.set(type, new Set());
  }
  messageHandlers.get(type)!.add(handler);

  // Return unsubscribe function
  return () => {
    messageHandlers.get(type)?.delete(handler);
  };
}

/**
 * Register a handler for all messages on a channel
 */
function onChannel(channel: string, handler: MessageHandler): () => void {
  if (!channelHandlers.has(channel)) {
    channelHandlers.set(channel, new Set());
  }
  channelHandlers.get(channel)!.add(handler);

  // Return unsubscribe function
  return () => {
    channelHandlers.get(channel)?.delete(handler);
  };
}

/**
 * Main useWebSocket composable
 */
export function useWebSocket() {
  return {
    // State
    isConnected: computed(() => isConnected.value),
    isConnecting: computed(() => isConnecting.value),
    connectionId: computed(() => connectionId.value),
    lastError: computed(() => lastError.value),
    reconnectAttempts: computed(() => reconnectAttempts.value),

    // Methods
    connect,
    disconnect,
    send,
    subscribeToHearing,
    unsubscribeFromHearing,
    onMessage,
    onChannel,

    // Constants
    MessageType,
    Channel,
  };
}

/**
 * Composable to auto-connect WebSocket when user is authenticated
 */
export function useAutoConnect() {
  const hasToken = computed(() => !!localStorage.getItem('access_token'));

  onMounted(() => {
    if (hasToken.value && !isConnected.value && !isConnecting.value) {
      connect();
    }
  });

  onUnmounted(() => {
    // Don't disconnect on unmount - keep connection alive
    // Connection is shared across components
  });

  // Watch for token changes
  watch(hasToken, (newValue) => {
    if (newValue && !isConnected.value && !isConnecting.value) {
      connect();
    } else if (!newValue && isConnected.value) {
      disconnect();
    }
  });

  return {
    isConnected: computed(() => isConnected.value),
  };
}
